import { Component, AfterViewInit, Inject } from '@angular/core';
import * as L from 'leaflet';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { GatewayService } from 'src/app/gateway.service';
import { MatSnackBar } from '@angular/material';
import { MapaValidacionDialogComponent } from './mapa-validacion-dialog/mapa-validacion-dialog.component';

delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
  iconUrl: 'assets/leaflet/marker-icon.png'
});

interface Provincia {
  idprovincia: number;
  nombre: string;
}

interface Locacion {
  id?: number;
  idcorregimiento?: number;
  iddistrito?: number;
  nombre: string;
}

interface MapaDialogData {
  lat?: number;
  lng?: number;
  provincia?: Provincia;
  distrito?: Locacion;
  corregimiento?: Locacion;
  cita?: any;
}

const PROVINCIA_COORDENADAS: { [key: number]: { lat: number; lng: number; zoom: number } } = {
  1: { lat: 9.34, lng: -82.25, zoom: 9 },
  2: { lat: 8.45, lng: -80.43, zoom: 9 },
  3: { lat: 9.36, lng: -79.90, zoom: 9 },
  4: { lat: 8.43, lng: -82.43, zoom: 9 },
  5: { lat: 7.88, lng: -77.80, zoom: 8 },
  6: { lat: 7.856324, lng: -80.749266, zoom: 10 },
  7: { lat: 7.52, lng: -80.38, zoom: 10 },
  8: { lat: 9.00, lng: -79.50, zoom: 10 },
  9: { lat: 8.87, lng: -79.83, zoom: 10 },
  10: { lat: 8.12, lng: -80.97, zoom: 9 },
};

// (LIMITAR)
const PANAMA_BOUNDS = L.latLngBounds(
  L.latLng(7.0, -83.2), // suroeste
  L.latLng(10.0, -77.0) // noreste
);

@Component({
  selector: 'app-mapa-dialog',
  templateUrl: './mapa-dialog.component.html',
  styleUrls: ['./mapa-dialog.component.scss']
})
export class MapaDialogComponent implements AfterViewInit {
  private cita: any = null;
  private map!: L.Map;
  private marker!: L.Marker;
  private poligonoLayer: L.GeoJSON | null = null;
  /** Flag config MAPA_VALIDAR_POLIGONO_OBLIGATORIO. true por defecto: si el
   *  pin cae fuera del polígono, bloqueamos. Se actualiza al abrir el dialog. */
  private validacionObligatoria: boolean = true;
  selectedLatLng: { lat: number; lng: number } | null = null;

  searchQuery: string = '';
  searchResults: any[] = [];
  private searchSubject = new Subject<string>();

  manualLat: number | null = null;
  manualLng: number | null = null;
  constructor(
    public dialogRef: MatDialogRef<MapaDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MapaDialogData,
    private http: HttpClient,
    private localSave: LocalSaveService,
    private gateway: GatewayService,
    private snackbar: MatSnackBar,
    private dialog: MatDialog
  ) {
    // Autocomplete por keystroke fue removido para no violar el TOS de
    // Nominatim (rate limit + prohibicion explicita de autocomplete). La
    // busqueda ahora se dispara unicamente al apretar Enter o el icono lupa
    // via onSearchSubmit(), que pega al proxy /api/PGV/LOCACION/GEOCODE.
  }

  // Indicador para deshabilitar el boton mientras el proxy responde.
  searching: boolean = false;

  ngAfterViewInit(): void {
    this.cita = this.data.cita;
    let lat = this.data.lat || 8.983333;
    let lng = this.data.lng || -79.516667;
    let zoom = 7;

    if (this.data.provincia && PROVINCIA_COORDENADAS[this.data.provincia.idprovincia]
      && !this.data.lat && !this.data.lng
    ) {
      const coord = PROVINCIA_COORDENADAS[this.data.provincia.idprovincia];
      lat = coord.lat;
      lng = coord.lng;
      zoom = coord.zoom;
    } else {
      lat = this.data.lat || lat;
      lng = this.data.lng || lng;
      zoom = 17;
    }

    if (this.cita) {
      this.map = L.map('map-dialog').setView([lat, lng], zoom);
    } else {
      // (LIMITAR)
      this.map = L.map('map-dialog', {
        maxBounds: PANAMA_BOUNDS,
        maxBoundsViscosity: 1.0
      }).setView([lat, lng], zoom);
    }

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap'
    }).addTo(this.map);

    if (this.data.provincia || (this.data.lat && this.data.lng)) {
      this.addOrUpdateMarker(lat, lng);
    }

    if (this.cita) {
      this.map.on('click', (e: any) => {
        const { lat, lng } = e.latlng;
        this.addOrUpdateMarker(lat, lng);
      });
    } else {
      // (LIMITAR) Evento click validado
      this.map.on('click', (e: any) => {
        const { lat, lng } = e.latlng;
        if (!PANAMA_BOUNDS.contains([lat, lng])) {
          this.snackbar.open('Ubicación fuera de Panamá', 'Cerrar', { duration: 3000 });
          return;
        }
        this.addOrUpdateMarker(lat, lng);
      });
    }

    // Cargar y pintar el polígono del corregimiento esperado (si lo conocemos)
    this.cargarYPintarPoligono();
    // Cargar el flag de validación obligatoria desde la tabla config
    this.cargarFlagValidacion();
  }

  /**
   * Lee el flag MAPA_VALIDAR_POLIGONO_OBLIGATORIO desde el backend para saber
   * si al confirmar fuera del polígono debemos bloquear (true) o sólo
   * advertir (false). Si falla la lectura, conservamos el default seguro
   * (true = bloquea).
   */
  private async cargarFlagValidacion(): Promise<void> {
    try {
      const r: any = await this.gateway.get('getConfigValidacionMapa', {});
      if (r && typeof r.obligatorio === 'boolean') {
        this.validacionObligatoria = r.obligatorio;
      }
    } catch (_) { /* mantener default true */ }
  }

  /**
   * Llama al backend para obtener el polígono del corregimiento esperado
   * (cita.id_corregimiento o data.corregimiento.id) y lo pinta en el mapa
   * como overlay semitransparente.
   *
   * Reglas de cámara:
   *   - Si el pin actual está DENTRO del polígono -> no toca zoom ni marker.
   *   - Si el pin actual está FUERA o no existe   -> reposiciona el marker
   *     al centro del polígono y hace fitBounds al polígono.
   *   - Si no hay polígono cargado para ese corregimiento -> no hace nada.
   */
  private async cargarYPintarPoligono(): Promise<void> {
    let idCorreg: number | null = null;
    if (this.cita) {
      idCorreg = this.cita.id_corregimiento || this.cita.idcorregimiento || this.cita.id_zona || null;
    } else if (this.data && this.data.corregimiento) {
      const c: any = this.data.corregimiento;
      idCorreg = c.id || c.idcorregimiento || null;
    }
    if (!idCorreg) return;

    try {
      const res: any = await this.gateway.get('getPoligonoLocacion', { id_locacion: idCorreg });
      if (!res || !res.geojson) return;
      // Crear capa Leaflet
      this.poligonoLayer = L.geoJSON(res.geojson, {
        style: {
          color: '#1976d2',
          weight: 2,
          opacity: 0.9,
          fillColor: '#1976d2',
          fillOpacity: 0.12,
        } as any,
        interactive: false,
      } as any).addTo(this.map);

      // Decidir cámara según si el pin actual cae dentro del polígono.
      const pinLat = this.selectedLatLng ? this.selectedLatLng.lat
                    : (this.data.lat != null ? this.data.lat : null);
      const pinLng = this.selectedLatLng ? this.selectedLatLng.lng
                    : (this.data.lng != null ? this.data.lng : null);

      const pinDentro = (pinLat != null && pinLng != null)
        ? this.puntoDentroDeGeoJson(res.geojson, pinLat, pinLng)
        : false;

      if (!pinDentro) {
        // El pin está fuera (o no existe). Reposicionamos al centro del
        // polígono y hacemos fitBounds.
        const centro = this.poligonoLayer.getBounds().getCenter();
        this.addOrUpdateMarker(centro.lat, centro.lng);
        try {
          this.map.fitBounds(this.poligonoLayer.getBounds(), {
            padding: [60, 60],
            maxZoom: 13,
          } as any);
        } catch (_) { /* noop */ }
      }
      // Si está dentro, no tocamos zoom ni marker.
    } catch (_) {
      // Silencioso: si falla, el mapa funciona igual sin overlay
    }
  }

  /** Ray-casting point-in-polygon sobre un GeoJSON Polygon o MultiPolygon. */
  private puntoDentroDeGeoJson(geojson: any, lat: number, lng: number): boolean {
    if (!geojson) return false;
    const x = lng, y = lat;
    const polyInside = (poly: number[][][]): boolean => {
      // poly = [outerRing, hole1, hole2, ...]
      const ringInside = (ring: number[][]): boolean => {
        let inside = false;
        for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
          const xi = ring[i][0], yi = ring[i][1];
          const xj = ring[j][0], yj = ring[j][1];
          const intersect = ((yi > y) !== (yj > y))
            && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi);
          if (intersect) inside = !inside;
        }
        return inside;
      };
      if (!ringInside(poly[0])) return false;
      // Si está dentro de algún hueco, queda fuera
      for (let i = 1; i < poly.length; i++) {
        if (ringInside(poly[i])) return false;
      }
      return true;
    };
    if (geojson.type === 'Polygon') return polyInside(geojson.coordinates);
    if (geojson.type === 'MultiPolygon')
      return (geojson.coordinates as number[][][][]).some(polyInside);
    return false;
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove(); // elimina listeners, capas y el mapa del DOM
    }
  }

  centrarEnCoordenadas(): void {
    if (this.manualLat !== null && this.manualLng !== null) {
      const lat = this.manualLat;
      const lng = this.manualLng;

      if (!this.cita) {
        // (LIMITAR) Verificar que no se mueva del pais de PANAMA
        if (!PANAMA_BOUNDS.contains([lat, lng])) {
          this.snackbar.open('Coordenadas fuera de Panamá', 'Cerrar', { duration: 3000 });
          return;
        }
      }

      this.map.setView([lat, lng], 17); // centra el mapa
      this.addOrUpdateMarker(lat, lng); // actualiza o crea el marcador
    }
  }

  private addOrUpdateMarker(lat: number, lng: number): void {
    this.selectedLatLng = { lat, lng };
    let popupContent = '';
    if (this.cita) {
      popupContent = `
        <b>${this.cita.status_name}</b><br>
        <b>ID Cita:</b> ${this.cita.idcita || ''}<br>
        <b>Prioridad:</b> ${this.cita.prioridad || ''}<br>
        <b>Provincia:</b> ${this.cita.provincia || ''}<br>
        <b>Localización:</b> ${this.cita.localizacion || ''}<br>
        <b>Dirección:</b> ${this.cita.address || ''}<br>
        <b>Técnico:</b> ${this.cita.tecnico || ''}<br>
        <b>Teléfono:</b> ${this.cita.telefono || ''}<br>
        <b>Contrata:</b> ${this.cita.contrata || ''}<br>
        <b>Fecha:</b> ${this.cita.date || ''} ${this.cita.am_pm || ''}<br>
        <b>Falla:</b> ${this.cita.falla || ''}<br>
        <b>Supervisor:</b> ${this.cita.supervisor || ''}<br>
        <b>Tel. Supervisor:</b> ${this.cita.tel_supervisor || ''}<br>
        <b>Jefe:</b> ${this.cita.jefe || ''}<br>
        <b>Tel. Jefe:</b> ${this.cita.tel_jefe || ''}<br>
        <b>Lat:</b> ${lat.toFixed(6)}<br>
        <b>Lng:</b> ${lng.toFixed(6)}`;
    } else {
      popupContent = `Lat: ${lat.toFixed(6)}<br>Lng: ${lng.toFixed(6)}`;
    }

    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
      this.marker.setPopupContent(popupContent).openPopup();
    } else {
      this.marker = L.marker([lat, lng], { draggable: true })
        .addTo(this.map)
        .bindPopup(popupContent)
        .openPopup();

      this.marker.on('dragend', (event: any) => {
        const pos = event.target.getLatLng();
        this.map.panTo(pos);
        this.addOrUpdateMarker(pos.lat, pos.lng);
      });
    }
  }

  // Dispara la busqueda contra el proxy del backend. Se llama desde el
  // (keyup.enter) del input y desde el click en el icono de lupa.
  onSearchSubmit(): void {
    const query = (this.searchQuery || '').trim();
    if (query.length < 3) {
      this.searchResults = [];
      this.snackbar.open('Ingresá al menos 3 caracteres para buscar', 'Cerrar', { duration: 3000 });
      return;
    }
    if (this.searching) { return; }
    this.searching = true;
    this.gateway.get('geocodeSearch', { query: query })
      .then((res: any) => {
        this.searching = false;
        const arr = res && Array.isArray(res.results) ? res.results : [];
        this.searchResults = arr;
        if (arr.length === 0) {
          var msg = 'Sin resultados';
          if (res && res.error === 'rate_limited') {
            msg = 'Servicio de búsqueda saturado, intentá de nuevo en unos segundos';
          } else if (res && res.error) {
            msg = 'Error en el servicio de búsqueda';
          }
          this.snackbar.open(msg, 'Cerrar', { duration: 3000 });
        }
      })
      .catch(() => {
        this.searching = false;
        this.searchResults = [];
        this.snackbar.open('Error al buscar dirección', 'Cerrar', { duration: 3000 });
      });
  }

  // 📍 Al seleccionar una sugerencia
  onOptionSelected(selected: any): void {
    const lat = parseFloat(selected.lat);
    const lng = parseFloat(selected.lon);

    // (LIMITAR)
    if (!PANAMA_BOUNDS.contains([lat, lng])) {
      this.snackbar.open('La ubicación seleccionada está fuera de Panamá', 'Cerrar', {
        duration: 4000
      });

      this.searchQuery = typeof selected.display_name === 'string'
      ? selected.display_name
      : '';

      return;
    }

    this.map.setView([lat, lng], 17);
    this.addOrUpdateMarker(lat, lng);
    this.searchQuery = selected.display_name;
    this.searchResults = [];
  }

  // 🏷️ Titulo dinamico para el dialog: "PROVINCIA - CORREGIMIENTO"
  tituloProvinciaCorregimiento(): string {
    const cita = this.cita || (this.data && this.data.cita);
    if (cita) {
      const loc = cita.localizacion;
      if (loc && typeof loc === 'string') {
        const partes = loc.split('-').map((p: string) => p.trim()).filter((p: string) => !!p);
        if (partes.length >= 3) {
          // Provincia + Corregimiento (omitimos distrito intermedio)
          return `${partes[0]} - ${partes[partes.length - 1]}`;
        }
        if (partes.length === 2) {
          return `${partes[0]} - ${partes[1]}`;
        }
        if (loc.trim().length > 0) {
          return loc;
        }
      }
      const provincia = cita.provincia || cita.nombre_provincia || '';
      const corregimiento = cita.corregimiento || cita.nombre_corregimiento || '';
      if (provincia && corregimiento) {
        return `${provincia} - ${corregimiento}`;
      }
      if (provincia) {
        return provincia;
      }
      if (corregimiento) {
        return corregimiento;
      }
    }
    // Modo agente (sin cita): usar data.provincia / data.distrito / data.corregimiento
    if (this.data) {
      const provNombre = (this.data.provincia && this.data.provincia.nombre) ? this.data.provincia.nombre : '';
      const corregNombre = (this.data.corregimiento && this.data.corregimiento.nombre) ? this.data.corregimiento.nombre : '';
      if (provNombre && corregNombre) {
        return `${provNombre} - ${corregNombre}`;
      }
      if (provNombre) {
        return provNombre;
      }
    }
    return 'Panamá';
  }

  // Normaliza texto (sin acentos, mayusculas, trim)
  private normTxt(s: string): string {
    return (s || '')
      .toString()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .trim()
      .toUpperCase();
  }

  // 🔎 Valida si las coordenadas caen dentro del corregimiento esperado.
  //
  // Estrategia (en este orden):
  //  1) Backend point-in-polygon contra `locacion_poligono` (fuente de verdad).
  //     - Si el corregimiento tiene polígono cargado: respuesta autoritativa
  //       (ok=true si dentro, ok=false si fuera).
  //     - Si NO tiene polígono cargado: el backend devuelve motivo='SIN_POLIGONO'
  //       y caemos al fallback Nominatim.
  //  2) Fallback Nominatim (heurística sobre reverse geocoding). Best-effort;
  //     no bloquea si Nominatim falla.
  private async validarCorregimiento(lat: number, lng: number): Promise<{ ok: boolean; motivo: 'OK'|'OK_HOLGURA'|'FUERA'|'SIN_POLIGONO'|'NO_VALIDABLE'; detectado: string; esperado: string; corregimientoDetectado: string; corregimientoEsperado: string; provinciaOk: boolean; corregimientoOk: boolean; distanciaMetros?: number; holguraMetros?: number; }> {
    const cita = this.cita || (this.data && this.data.cita);
    const vacio: any = { ok: true, motivo: 'NO_VALIDABLE', detectado: '', esperado: '', corregimientoDetectado: '', corregimientoEsperado: '', provinciaOk: true, corregimientoOk: true };

    // Resolver provincia/corregimiento esperado: prioriza cita; si no, usa data del agente
    let provinciaEsperada = '';
    let corregimientoEsperado = '';
    let idCorregimiento: number | null = null;

    if (cita) {
      provinciaEsperada = cita.provincia || cita.nombre_provincia || '';
      corregimientoEsperado = cita.corregimiento || cita.nombre_corregimiento || '';
      idCorregimiento = cita.id_corregimiento || cita.idcorregimiento || cita.id_zona || null;
      if (!corregimientoEsperado && cita.localizacion && typeof cita.localizacion === 'string') {
        const partes = cita.localizacion.split('-').map((p: string) => p.trim()).filter((p: string) => !!p);
        if (partes.length >= 3) {
          corregimientoEsperado = partes[partes.length - 1];
        }
      }
    } else if (this.data) {
      // Modo agente: formulario con provincia/distrito/corregimiento del FormGroup
      provinciaEsperada = (this.data.provincia && this.data.provincia.nombre) ? this.data.provincia.nombre : '';
      corregimientoEsperado = (this.data.corregimiento && this.data.corregimiento.nombre) ? this.data.corregimiento.nombre : '';
      if (this.data.corregimiento) {
        const c: any = this.data.corregimiento;
        idCorregimiento = c.id || c.idcorregimiento || null;
      }
    }

    if (!provinciaEsperada && !corregimientoEsperado) {
      return vacio;
    }

    // === 1) Backend point-in-polygon ===
    if (idCorregimiento) {
      try {
        const res: any = await this.gateway.get('validarCoordsCorregimiento', {
          id_corregimiento: idCorregimiento,
          lat,
          lng,
        });
        if (res && res.motivo === 'OK') {
          return {
            ok: true,
            motivo: 'OK',
            detectado: '',
            esperado: provinciaEsperada,
            corregimientoDetectado: res.corregimiento_detectado ? res.corregimiento_detectado.nombre : corregimientoEsperado,
            corregimientoEsperado,
            provinciaOk: true,
            corregimientoOk: true,
            distanciaMetros: res.distancia_borde_metros,
            holguraMetros: res.holgura_metros,
          };
        }
        if (res && res.motivo === 'OK_HOLGURA') {
          return {
            ok: true,
            motivo: 'OK_HOLGURA',
            detectado: '',
            esperado: provinciaEsperada,
            corregimientoDetectado: res.corregimiento_detectado ? res.corregimiento_detectado.nombre : '',
            corregimientoEsperado,
            provinciaOk: true,
            corregimientoOk: true,
            distanciaMetros: res.distancia_borde_metros,
            holguraMetros: res.holgura_metros,
          };
        }
        if (res && res.motivo === 'FUERA_DEL_CORREGIMIENTO') {
          return {
            ok: false,
            motivo: 'FUERA',
            detectado: '',
            esperado: provinciaEsperada,
            corregimientoDetectado: res.corregimiento_detectado ? res.corregimiento_detectado.nombre : '',
            corregimientoEsperado,
            provinciaOk: true,
            corregimientoOk: false,
          };
        }
        if (res && res.motivo === 'SIN_POLIGONO') {
          // El backend nos dijo claramente que no puede validar este
          // corregimiento. NO caemos al fallback Nominatim (que da
          // falsos positivos para muchos corregimientos panameños).
          // Devolvemos ok=true con motivo SIN_POLIGONO para que la UI
          // muestre snackbar informativo y permita guardar.
          return {
            ok: true,
            motivo: 'SIN_POLIGONO',
            detectado: '',
            esperado: provinciaEsperada,
            corregimientoDetectado: '',
            corregimientoEsperado,
            provinciaOk: true,
            corregimientoOk: true,
          };
        }
        // NO_VALIDABLE u otro: caer al fallback Nominatim
      } catch (e) {
        // Si el endpoint falla, también caemos al fallback
      }
    }

    // === 2) Fallback Nominatim (heurística) ===
    try {
      const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=14&accept-language=es`;
      const result: any = await this.http.get(url).toPromise();
      const address = (result && result.address) ? result.address : {};
      const provinciaDetectada = address.state || address.region || address.province || '';
      const candidatosCorreg = [
        address.suburb, address.neighbourhood, address.village, address.town,
        address.hamlet, address.city_district, address.quarter, address.locality,
        address.county, address.municipality, address.city,
      ].filter((x: any) => !!x);
      const corregimientoDetectado = candidatosCorreg[0] || '';

      if (!provinciaDetectada && candidatosCorreg.length === 0) {
        return { ok: true, motivo: 'NO_VALIDABLE', detectado: '', esperado: provinciaEsperada, corregimientoDetectado: '', corregimientoEsperado, provinciaOk: true, corregimientoOk: true };
      }

      // Match de provincia
      let provinciaOk = true;
      if (provinciaEsperada && provinciaDetectada) {
        const a = this.normTxt(provinciaEsperada);
        const b = this.normTxt(provinciaDetectada);
        provinciaOk = a === b || a.includes(b) || b.includes(a);
      }

      // Match de corregimiento (heurística display_name + jerarquía)
      let corregimientoOk = true;
      const displayName: string = (result && result.display_name) ? result.display_name : '';
      if (corregimientoEsperado) {
        const esperadoNorm = this.normTxt(corregimientoEsperado);
        const displayNorm = this.normTxt(displayName);

        const matchEnCandidatos = candidatosCorreg.some((c: string) => {
          const det = this.normTxt(c);
          return det === esperadoNorm || det.includes(esperadoNorm) || esperadoNorm.includes(det);
        });

        const esGenerico = (n: string) =>
          !n || n === 'PANAMA'
          || n.startsWith('DISTRITO DE')
          || n.startsWith('PROVINCIA DE')
          || n.startsWith('PROVINCIA ');
        const candidatosClaros = candidatosCorreg
          .map((c: string) => this.normTxt(c))
          .filter((n: string) => !esGenerico(n) && n !== esperadoNorm);

        if (matchEnCandidatos) {
          corregimientoOk = true;
        } else if (candidatosClaros.length > 0) {
          if (displayNorm && displayNorm.includes(esperadoNorm)) {
            const idxDetectado = displayNorm.indexOf(candidatosClaros[0]);
            const idxEsperado = displayNorm.indexOf(esperadoNorm);
            corregimientoOk = idxDetectado >= 0 && idxEsperado > idxDetectado;
          } else {
            corregimientoOk = false;
          }
        } else if (displayNorm && displayNorm.includes(esperadoNorm)) {
          corregimientoOk = true;
        } else if (candidatosCorreg.length === 0 && !displayName) {
          corregimientoOk = true;
        } else {
          corregimientoOk = false;
        }
      }

      const okFinal = provinciaOk && corregimientoOk;
      return {
        ok: okFinal,
        motivo: okFinal ? 'OK' : 'FUERA',
        detectado: provinciaDetectada,
        esperado: provinciaEsperada,
        corregimientoDetectado,
        corregimientoEsperado,
        provinciaOk,
        corregimientoOk,
      };
    } catch (e) {
      return { ok: true, motivo: 'NO_VALIDABLE', detectado: '', esperado: provinciaEsperada, corregimientoDetectado: '', corregimientoEsperado, provinciaOk: true, corregimientoOk: true };
    }
  }

  async confirmarUbicacion(): Promise<void> {
    if (!this.selectedLatLng) {
      this.dialogRef.close(null);
      return;
    }

    // Determinar si tenemos suficiente info para validar contra un corregimiento
    // (modo cita: data.cita; modo agente: data.corregimiento del FormGroup)
    const corregimientoAgente = this.data && this.data.corregimiento && this.data.corregimiento.nombre;
    const debeValidar = !!this.cita || !!corregimientoAgente;

    if (debeValidar) {
      const validacion = await this.validarCorregimiento(this.selectedLatLng.lat, this.selectedLatLng.lng);

      // Caso SIN_POLIGONO: el corregimiento esperado no tiene polígono
      // cargado en BD. Mostramos snackbar informativo pero no bloqueamos
      // (no podemos juzgar dónde es).
      if (validacion.motivo === 'SIN_POLIGONO') {
        this.snackbar.open(
          `No tenemos el polígono de "${validacion.corregimientoEsperado || 'este corregimiento'}", se guarda sin validar la ubicación.`,
          'Cerrar',
          { duration: 5000 }
        );
      } else if (validacion.motivo === 'OK_HOLGURA') {
        // Está fuera del polígono pero dentro de la holgura tolerada.
        const dist = validacion.distanciaMetros != null
          ? Math.round(validacion.distanciaMetros) : '?';
        this.snackbar.open(
          `Estás en el límite del corregimiento (a ${dist}m del borde). Se guarda igual.`,
          'Cerrar',
          { duration: 5000 }
        );
      } else if (!validacion.ok) {
        // FUERA del polígono. Decisión depende del flag MAPA_VALIDAR_POLIGONO_OBLIGATORIO.
        if (this.validacionObligatoria) {
          // OBLIGATORIO -> bloquea con dialog Material, NO guarda.
          this.dialog.open(MapaValidacionDialogComponent, {
            width: '500px',
            disableClose: false,
            data: {
              provinciaEsperada: validacion.esperado,
              provinciaDetectada: validacion.detectado,
              corregimientoEsperado: validacion.corregimientoEsperado,
              corregimientoDetectado: validacion.corregimientoDetectado,
              lat: this.selectedLatLng.lat,
              lng: this.selectedLatLng.lng,
            }
          });
          return; // NO cierra el mapa
        } else {
          // NO obligatorio -> sólo advertencia (snackbar), pero deja guardar.
          const det = validacion.corregimientoDetectado
            ? ` (cae en "${validacion.corregimientoDetectado}")` : '';
          this.snackbar.open(
            `La ubicación está fuera de "${validacion.corregimientoEsperado || 'el corregimiento esperado'}"${det}. Guardando igual.`,
            'Cerrar',
            { duration: 5000 }
          );
        }
      }
    }

    if (this.cita) {
      const params = {
        ...this.cita,
        LATITUD: this.selectedLatLng.lat,
        LONGITUD: this.selectedLatLng.lng,
        IDDISTRIBUIDOR: this.localSave.get('usuarioPGV').IDU,
        IDUSUARIO: this.localSave.get('usuarioPGV').IDU,
        NOMBRE_USUARIO: this.localSave.get('userLoggedIn') || null,
        CORREO_USUARIO: this.localSave.get('email') || null,
      };
      this.gateway.get('updateUbicacionCita', params).then((result) => {
        const aux: any = result;
        this.snackbar.open(aux.message, 'Cerrar', { duration: 5000 });
      }).catch(function (e) { });
    }
    this.dialogRef.close(this.selectedLatLng || null);
  }

  onNoClick() {
    this.dialogRef.close();
  }
}
